(() => {
  const SELECT_EVENT = 'slideshow:select';
  const SHELL_SELECTOR = '.khushi-product-gallery-shell[data-khushi-enabled="true"]';
  const PILL_STYLE = 'carousel_dots';
  const MOBILE_FIRST_VIEW_MEDIA = window.matchMedia('(max-width: 749px)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileFirstViewLocked = new WeakSet();
  const states = new WeakMap();

  function visibleSlides(slideshow) {
    return Array.from(slideshow.querySelectorAll('slideshow-slide')).filter((slide) => {
      if (slide.closest('slideshow-component') !== slideshow) return false;
      return !slide.hasAttribute('hidden') || slide.hasAttribute('reveal');
    });
  }

  function slideSignature(slides) {
    return slides
      .map((slide, index) => slide.getAttribute('slide-id') || slide.id || `visible-${index}`)
      .join('|');
  }

  function carouselDuration(shell) {
    const localDuration = Number(shell.dataset.khushiCarouselDuration);
    if (Number.isFinite(localDuration)) return Math.min(7000, Math.max(3000, localDuration));
    return 4000;
  }

  function effectiveGalleryStyle(shell) {
    if (MOBILE_FIRST_VIEW_MEDIA.matches) return PILL_STYLE;
    return shell.dataset.khushiGalleryStyle || 'vertical_thumbnails';
  }

  function findPageBackground(shell) {
    let node = shell.parentElement;
    while (node && node !== document.documentElement) {
      const color = getComputedStyle(node).backgroundColor;
      if (color && color !== 'rgba(0, 0, 0, 0)' && color !== 'transparent') return color;
      node = node.parentElement;
    }
    return getComputedStyle(document.body).backgroundColor;
  }

  function updateAutoBorder(shell) {
    const mode = shell.dataset.khushiBorderMode || 'auto';
    shell.classList.remove('khushi-gallery--border-active');
    if (mode === 'always') {
      shell.classList.add('khushi-gallery--border-active');
      return;
    }
    if (mode === 'never') return;
    const canvas = getComputedStyle(shell).backgroundColor;
    const page = findPageBackground(shell);
    if (canvas === page || canvas === 'rgba(0, 0, 0, 0)' || canvas === 'transparent') shell.classList.add('khushi-gallery--border-active');
  }

  function makeThumbnail(slide, index) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'khushi-gallery-control';
    button.dataset.khushiIndex = String(index);
    const source = slide.querySelector('.product-media__image, .deferred-media__poster-image, img');
    const alt = source instanceof HTMLImageElement ? source.alt : '';
    button.setAttribute('aria-label', alt ? `View ${alt}` : `View product media ${index + 1}`);
    if (source instanceof HTMLImageElement) {
      const image = document.createElement('img');
      image.className = 'khushi-gallery-control__image';
      image.src = source.currentSrc || source.src;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      button.appendChild(image);
    } else {
      const fallback = document.createElement('span');
      fallback.className = 'khushi-gallery-control__fallback';
      fallback.textContent = String(index + 1);
      button.appendChild(fallback);
    }
    return button;
  }

  function makePill(index) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'khushi-gallery-control khushi-gallery-control--pill';
    button.dataset.khushiIndex = String(index);
    button.setAttribute('aria-label', `Go to product media ${index + 1}`);
    return button;
  }

  function keepControlVisible(state, button) {
    if (!(button instanceof HTMLElement) || state.controls.hidden || state.style === PILL_STYLE) return;
    const controlsRect = state.controls.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const isVertical = getComputedStyle(state.controls).flexDirection === 'column';
    const behavior = REDUCED.matches ? 'auto' : 'smooth';
    if (isVertical) {
      let delta = 0;
      if (buttonRect.top < controlsRect.top) delta = buttonRect.top - controlsRect.top;
      else if (buttonRect.bottom > controlsRect.bottom) delta = buttonRect.bottom - controlsRect.bottom;
      if (delta) state.controls.scrollTo({ top: state.controls.scrollTop + delta, behavior });
      return;
    }
    let delta = 0;
    if (buttonRect.left < controlsRect.left) delta = buttonRect.left - controlsRect.left;
    else if (buttonRect.right > controlsRect.right) delta = buttonRect.right - controlsRect.right;
    if (delta) state.controls.scrollTo({ left: state.controls.scrollLeft + delta, behavior });
  }

  function currentIndex(state) {
    const current = Number(state.slideshow.current);
    if (!state.slides.length || !Number.isFinite(current)) return 0;
    return Math.min(state.slides.length - 1, Math.max(0, current));
  }

  function syncControls(state, index = currentIndex(state)) {
    state.controls.querySelectorAll('.khushi-gallery-control').forEach((button) => {
      const selected = Number(button.dataset.khushiIndex) === index;
      button.setAttribute('aria-current', selected ? 'true' : 'false');
      if (selected) keepControlVisible(state, button);
    });
  }

  function stopProgress(state) {
    state.controls.querySelectorAll('.khushi-gallery-control--pill').forEach((button) => button.classList.remove('khushi-gallery-progress-running'));
  }

  function restartProgress(state) {
    stopProgress(state);
    const current = state.controls.querySelector('.khushi-gallery-control--pill[aria-current="true"]');
    if (!(current instanceof HTMLElement)) return;
    current.dataset.khushiProgressCycle = current.dataset.khushiProgressCycle === 'a' ? 'b' : 'a';
    current.classList.add('khushi-gallery-progress-running');
  }

  function restoreNativeState(state) {
    if (!state.nativeOverrideActive) return;
    state.slideshow.suspend?.();
    state.slideshow.toggleAttribute('paused', state.originalPaused);
    if (state.originalAutoplay == null) state.slideshow.removeAttribute('autoplay');
    else state.slideshow.setAttribute('autoplay', state.originalAutoplay);
    if (state.originalInfinite) state.slideshow.setAttribute('infinite', '');
    else state.slideshow.removeAttribute('infinite');
    if (state.originalAriaLive == null) state.slideshow.removeAttribute('aria-live');
    else state.slideshow.setAttribute('aria-live', state.originalAriaLive);
    state.shell.dataset.khushiNativeAutoplay = 'false';
    state.nativeOverrideActive = false;
    stopProgress(state);
    if (state.originalAutoplay != null && !state.originalPaused && !REDUCED.matches) state.slideshow.play?.();
  }

  function shouldAutoplay(state) {
    return Boolean(state.style === PILL_STYLE && state.slides.length > 1 && !REDUCED.matches && !document.hidden);
  }

  function startNativeAutoplay(state) {
    if (state.style !== PILL_STYLE || state.slides.length <= 1) {
      restoreNativeState(state);
      return;
    }
    state.nativeOverrideActive = true;
    state.slideshow.setAttribute('infinite', '');
    state.shell.style.setProperty('--khushi-gallery-pill-duration', `${state.pillDuration}ms`);
    if (!shouldAutoplay(state)) {
      state.slideshow.suspend?.();
      state.slideshow.removeAttribute('autoplay');
      state.slideshow.setAttribute('paused', '');
      state.shell.dataset.khushiNativeAutoplay = 'false';
      stopProgress(state);
      return;
    }
    const seconds = Math.max(1, Math.round(state.pillDuration / 1000));
    state.slideshow.setAttribute('autoplay', String(seconds));
    state.slideshow.setAttribute('aria-live', 'polite');
    state.slideshow.removeAttribute('paused');
    state.shell.dataset.khushiNativeAutoplay = 'true';
    state.slideshow.suspend?.();
    state.slideshow.play?.();
    restartProgress(state);
  }

  function scheduleNativeRestart(state, delay = 180) {
    if (state.restartTimer) window.clearTimeout(state.restartTimer);
    state.restartTimer = window.setTimeout(() => {
      state.restartTimer = 0;
      if (!state.shell.isConnected || !state.slideshow.isConnected) return;
      startNativeAutoplay(state);
    }, delay);
  }

  function bindControl(button, state, index) {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (state.style === PILL_STYLE) {
        state.slideshow.suspend?.();
        stopProgress(state);
      }
      state.slideshow.select?.(index, event, { animate: !REDUCED.matches });
      if (state.style === PILL_STYLE) scheduleNativeRestart(state, 100);
    }, { signal: state.controlAbort.signal });
  }

  function rebuildControls(state, slides) {
    state.controlAbort.abort();
    state.controlAbort = new AbortController();
    state.controls.replaceChildren();
    slides.forEach((slide, index) => {
      const button = state.style === PILL_STYLE ? makePill(index) : makeThumbnail(slide, index);
      bindControl(button, state, index);
      state.controls.appendChild(button);
    });
    state.controls.hidden = slides.length <= 1;
    syncControls(state);
  }

  function refreshState(state, force = false) {
    if (!state.shell.isConnected || !state.slideshow.isConnected || !state.controls.isConnected) return;
    const style = effectiveGalleryStyle(state.shell);
    state.shell.dataset.khushiEffectiveGalleryStyle = style;
    const duration = carouselDuration(state.shell);
    const slides = visibleSlides(state.slideshow);
    const signature = slideSignature(slides);
    const styleChanged = state.style !== style;
    const slidesChanged = state.slideSignature !== signature;
    const durationChanged = state.pillDuration !== duration;
    state.style = style;
    state.pillDuration = duration;
    state.shell.style.setProperty('--khushi-gallery-pill-duration', `${duration}ms`);
    if (force || styleChanged || slidesChanged) {
      state.slides = slides;
      state.slideSignature = signature;
      rebuildControls(state, slides);
    } else {
      state.slides = slides;
      syncControls(state);
    }
    if (force || styleChanged || slidesChanged || durationChanged) startNativeAutoplay(state);
  }

  function scheduleRefresh(state, force = false) {
    state.needsForceRefresh ||= force;
    if (state.refreshFrame) return;
    state.refreshFrame = window.requestAnimationFrame(() => {
      state.refreshFrame = 0;
      const shouldForce = state.needsForceRefresh;
      state.needsForceRefresh = false;
      refreshState(state, shouldForce);
    });
  }

  function clearMobileFirstView(shell) {
    shell.removeAttribute('data-khushi-mobile-first-view');
    shell.removeAttribute('data-khushi-mobile-first-view-locked');
    shell.style.removeProperty('--khushi-mobile-first-view-media-height');
  }

  function findMobileFirstViewTargets(shell) {
    const productInformation = shell.closest('.product-information');
    const mediaColumn = shell.closest('.product-information__media');
    const details = productInformation?.querySelector('.product-details');
    const title = details?.querySelector('h1');
    const price = details?.querySelector('product-price');
    const slideshow = shell.querySelector('media-gallery > slideshow-component');
    const slideshowContainer = slideshow?.querySelector(':scope > slideshow-container');
    if (!(productInformation instanceof HTMLElement) || !(mediaColumn instanceof HTMLElement) || !(details instanceof HTMLElement) || !(title instanceof HTMLElement) || !(price instanceof HTMLElement) || !(slideshow instanceof HTMLElement) || !(slideshowContainer instanceof HTMLElement)) return null;
    return { productInformation, mediaColumn, details, title, price, slideshow, slideshowContainer };
  }

  function cssPixelValue(value) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function lockMobileFirstView(shell) {
    shell.setAttribute('data-khushi-mobile-first-view-locked', 'true');
    mobileFirstViewLocked.add(shell);
  }

  function updateMobileFirstView(shell, initialViewportHeight) {
    if (!(shell instanceof HTMLElement)) return false;
    if (mobileFirstViewLocked.has(shell)) return true;
    if (!shell.isConnected || !MOBILE_FIRST_VIEW_MEDIA.matches) return false;
    const targets = findMobileFirstViewTargets(shell);
    if (!targets) return false;
    clearMobileFirstView(shell);
    const shellRect = shell.getBoundingClientRect();
    const mediaColumnRect = targets.mediaColumn.getBoundingClientRect();
    const detailsRect = targets.details.getBoundingClientRect();
    const priceRect = targets.price.getBoundingClientRect();
    const slideshowContainerRect = targets.slideshowContainer.getBoundingClientRect();
    const viewportHeight = initialViewportHeight;
    if (!Number.isFinite(viewportHeight) || viewportHeight <= 0) return false;
    if (shellRect.height <= 0 || slideshowContainerRect.height <= 0 || priceRect.height <= 0) return false;
    const shellDocumentTop = Math.max(0, shellRect.top + (window.scrollY || 0));
    const detailsGap = Math.max(0, detailsRect.top - mediaColumnRect.bottom);
    const infoReserve = Math.max(0, priceRect.bottom - detailsRect.top);
    const nonMediaReserve = Math.max(0, shellRect.height - slideshowContainerRect.height);
    const rootStyles = getComputedStyle(document.documentElement);
    const breathingSpace = cssPixelValue(rootStyles.getPropertyValue('--padding-sm')) || 12;
    const availableShellHeight = viewportHeight - shellDocumentTop - detailsGap - infoReserve - breathingSpace;
    const availableMediaHeight = availableShellHeight - nonMediaReserve;
    const minimumMediaHeight = Math.max(180, Math.min(280, viewportHeight * 0.34));
    const targetMediaHeight = Math.max(minimumMediaHeight, Math.floor(availableMediaHeight));
    if (shellRect.height <= availableShellHeight + 1 || slideshowContainerRect.height <= targetMediaHeight + 1) {
      lockMobileFirstView(shell);
      return true;
    }
    shell.style.setProperty('--khushi-mobile-first-view-media-height', `${targetMediaHeight}px`);
    shell.setAttribute('data-khushi-mobile-first-view', 'true');
    lockMobileFirstView(shell);
    return true;
  }

  function setupMobileFirstView(shell) {
    if (!MOBILE_FIRST_VIEW_MEDIA.matches || mobileFirstViewLocked.has(shell)) return;
    const initialViewportHeight = window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight;
    let attempts = 0;
    const maxAttempts = 4;
    const tryInitialLock = () => {
      if (mobileFirstViewLocked.has(shell) || !shell.isConnected) return;
      if (updateMobileFirstView(shell, initialViewportHeight)) return;
      attempts += 1;
      if (attempts < maxAttempts) window.requestAnimationFrame(tryInitialLock);
    };
    window.requestAnimationFrame(() => window.requestAnimationFrame(tryInitialLock));
    const firstImage = shell.querySelector('media-gallery slideshow-slide img, media-gallery .product-media img');
    if (firstImage instanceof HTMLImageElement && !firstImage.complete) {
      const retryAfterImage = () => { if (!mobileFirstViewLocked.has(shell)) window.requestAnimationFrame(tryInitialLock); };
      firstImage.addEventListener('load', retryAfterImage, { once: true });
      firstImage.addEventListener('error', retryAfterImage, { once: true });
    }
    if (document.fonts?.ready) document.fonts.ready.then(() => { if (!mobileFirstViewLocked.has(shell)) window.requestAnimationFrame(tryInitialLock); });
  }

  function teardownState(state) {
    if (state.restartTimer) window.clearTimeout(state.restartTimer);
    if (state.refreshFrame) window.cancelAnimationFrame(state.refreshFrame);
    state.abort.abort();
    state.controlAbort.abort();
    state.mediaObserver.disconnect();
    state.resizeObserver.disconnect();
    restoreNativeState(state);
    states.delete(state.shell);
  }

  function createState(shell, slideshow, controls) {
    const state = {
      shell, slideshow, controls, style: '', pillDuration: 4000, slides: [], slideSignature: '', restartTimer: 0, refreshFrame: 0,
      needsForceRefresh: false, nativeOverrideActive: false,
      originalAutoplay: slideshow.getAttribute('autoplay'), originalInfinite: slideshow.hasAttribute('infinite'),
      originalAriaLive: slideshow.getAttribute('aria-live'), originalPaused: slideshow.hasAttribute('paused'),
      abort: new AbortController(), controlAbort: new AbortController(), mediaObserver: null, resizeObserver: null,
    };
    slideshow.addEventListener(SELECT_EVENT, (event) => {
      state.slides = visibleSlides(slideshow);
      syncControls(state, Number(event.detail?.index ?? slideshow.current ?? 0));
      if (state.style !== PILL_STYLE) return;
      if (event.detail?.userInitiated) {
        stopProgress(state);
        scheduleNativeRestart(state, event.detail?.trigger === 'scroll' ? 220 : 120);
      } else if (shouldAutoplay(state)) restartProgress(state);
    }, { signal: state.abort.signal });
    state.mediaObserver = new MutationObserver((records) => {
      if (!records.some((record) => record.type === 'childList' || record.attributeName === 'hidden')) return;
      scheduleRefresh(state);
    });
    state.mediaObserver.observe(slideshow, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden'] });
    state.resizeObserver = new ResizeObserver(() => updateAutoBorder(shell));
    state.resizeObserver.observe(shell);
    states.set(shell, state);
    updateAutoBorder(shell);
    refreshState(state, true);
    setupMobileFirstView(shell);
    return state;
  }

  function initShell(shell) {
    if (!(shell instanceof HTMLElement)) return;
    const slideshow = shell.querySelector('media-gallery > slideshow-component');
    const controls = shell.querySelector(':scope > [data-khushi-gallery-controls]');
    if (!(slideshow instanceof HTMLElement) || !(controls instanceof HTMLElement)) return;
    shell.removeAttribute('data-khushi-sticky-ready');
    shell.style.removeProperty('--khushi-gallery-sticky-top');
    const existing = states.get(shell);
    if (existing) {
      if (existing.slideshow !== slideshow || existing.controls !== controls || !slideshow.isConnected) {
        teardownState(existing);
        createState(shell, slideshow, controls);
        return;
      }
      updateAutoBorder(shell);
      scheduleRefresh(existing);
      return;
    }
    createState(shell, slideshow, controls);
  }

  function initAll(root = document) {
    customElements.whenDefined('slideshow-component').then(() => {
      if (root instanceof Element && root.matches(SHELL_SELECTOR)) initShell(root);
      root.querySelectorAll?.(SHELL_SELECTOR).forEach(initShell);
    });
  }

  function refreshAll() {
    document.querySelectorAll(SHELL_SELECTOR).forEach((shell) => {
      initShell(shell);
      const state = states.get(shell);
      if (state) scheduleRefresh(state, true);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:select', refreshAll);
  document.addEventListener('visibilitychange', refreshAll);
  if (typeof MOBILE_FIRST_VIEW_MEDIA.addEventListener === 'function') MOBILE_FIRST_VIEW_MEDIA.addEventListener('change', refreshAll);
  else MOBILE_FIRST_VIEW_MEDIA.addListener?.(refreshAll);
  if (typeof REDUCED.addEventListener === 'function') REDUCED.addEventListener('change', refreshAll);
  else REDUCED.addListener?.(refreshAll);
})();
